import {Icon} from '@wandb/weave/components/Icon';
import {clsx} from 'clsx';
import * as React from 'react';

import {ProjectTags2Document, type RunTag} from '../../generated/graphql';
import {makeInfiniteDropdown} from './InfiniteDropdown';

const Container = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
  }
>(({children}, ref) => {
  return <div ref={ref}>{children}</div>;
});

const Display = () => null;

const ListWrapper = React.forwardRef<
  HTMLUListElement,
  {
    children: React.ReactNode;
    isOpen: boolean;
  }
>((props, ref) => {
  const {children, isOpen, ...otherProps} = props;
  const classes = clsx('w-[300px] list-none overflow-auto p-0 max-h-[300px]');

  if (!isOpen) {
    return null;
  }

  return (
    <ul className={classes} ref={ref} {...otherProps}>
      {props.children}
    </ul>
  );
});

const ListItem = React.forwardRef<
  HTMLLIElement,
  {
    isHighlighted: boolean;
    isSelected: boolean;
    value: string;
  }
>((props, ref) => {
  const {isHighlighted, ...otherProps} = props;
  const classes = clsx(
    'hover:bg-[rgba(0,0,0,.05)] hover:text-[rgba(0,0,0,.95)]',
    'truncate leading-18 m-0 px-12 py-[13px] cursor-pointer item',
    {'bg-[rgba(0,0,0,.05)] text-[rgba(0,0,0,.95)]': isHighlighted}
  );
  return (
    <li
      ref={ref}
      data-test="run-tag-infinite-list-item"
      className={classes}
      {...otherProps}
    />
  );
});

const ItemLabel = ({
  isSelected,
  label,
}: {
  isSelected: boolean;
  label: string;
}) => {
  return (
    <div className="flex w-full justify-between">
      <span className="truncate text-base">{label}</span>
      {isSelected && <Icon name="checkmark" />}
    </div>
  );
};

const InputElement = React.forwardRef((props: any, ref) => {
  const {className, ...otherProps} = props;

  return (
    <div className="flex-column relative flex w-full items-center p-1 text-base">
      <Icon
        className="absolute ml-[8px] h-[16px] w-[16px] text-moon-500"
        name="search"
      />
      <input
        className="m-0 w-full border-0 py-5 pl-26 pr-12 text-base outline outline-1 outline-teal-400 dark:outline-teal-600"
        ref={ref}
        data-test="run-tag-infinite-input-field"
        placeholder="Search tags"
        {...otherProps}
        // this has to come after the spread to avoid accidental overwriting
        onClick={e => {
          e.stopPropagation();
        }}
        onKeyDown={event => {
          switch (event.key) {
            case 'Enter': {
              // State change doesn't work for some reason in downshift....
              event.preventDefault();
              props.toggleSelection(event.currentTarget.value);
            }
          }
        }}
      />
    </div>
  );
});

export const RunTagInfiniteDropdown = makeInfiniteDropdown(
  {
    Container,
    Display,
    Input: InputElement,
    ListItem,
    ItemLabel,
    ListWrapper,
  },
  {
    triggerExternally: true,
    extractItems: (data: any): string[] => {
      if (!data) {
        return [];
      }
      return (
        data?.project?.runTags?.edges?.map(
          (edge: {node: RunTag}) => edge.node.name
        ) ?? []
      );
    },
    extractPageInfo: (data: any) => {
      if (!data) {
        return {};
      }

      return data?.project?.runTags?.pageInfo ?? {};
    },
    query: ProjectTags2Document,
  }
);
